import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Slick } from '@/Infrastructure/slick';
import { Checkbox, IconButton, MobileStepper, useMediaQuery, useTheme } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
	GridColDefExtended,
	GridPinnedColumnFields,
	GridRowId,
	gridRowIdSelector,
	gridRowSelectionIdsSelector,
	useGridApiContext
} from '@mui/x-data-grid-pro';
import { defaultPinnedColumns, dispatchColumnDefinitions } from '../columns';
import RenderCardCell from '../../RenderCardCell';

import styles from './DispatchCard.module.css';

interface DispatchCardProps {
	/** Current data row to render */
	leg: DispatchLeg;
	/** Columns that should be pinned as defined by the current Board Query */
	gridPinnedColumns: GridPinnedColumnFields;
	/** Available columns as defined by the current Board Query */
	gridColumnDetails: GridColDefExtended<DispatchLeg>[];
	/** Index of the displaying "slide" */
	activeGroup: number;
	/** Dispatch event to change the displaying "slide" */
	setActiveGroup: Dispatch<SetStateAction<number>>;
	renderEditable?: (target: EventTarget & HTMLElement, columnDetails: GridColDefExtended<DispatchLeg>, row: DispatchLeg) => void;
}

/**
 * Component representing a 'row' of the Dispatch Board in a Card format for mobile view.
 */
export default function DispatchCard({ activeGroup, setActiveGroup, ...props }: DispatchCardProps) {
	const gridApiRef = useGridApiContext();
	const slickRef = useRef<Slick>(null);
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down('xs'));
	const availableSlots = !isSmall ? 4 : 2;

	useEffect(() => {
		slickRef.current?.slickGoTo(activeGroup);
	}, [activeGroup]);

	/** Pinned columns DispatchCard renders explicitly (checkbox/menu have no card cell, Order Number is rendered on its own) and must not be rendered again as a generic pinned column */
	const explicitlyRenderedPinnedColumns = ['__check__', 'actionMenu', 'orderNumber'];

	/** The columns that will be rendered in the groups (not pinned columns) */
	const renderingColumns = props.gridColumnDetails.filter(
		(item) => !defaultPinnedColumns.left?.includes(item.field) && !props.gridPinnedColumns.left?.includes(item.field)
	);
	/** How many swipeable groups we will have. Each group can host up to 4 columns, unless the screen is xs then 2 columns */
	const groupCount = Math.ceil(renderingColumns.length / availableSlots);
	const gridRowId: GridRowId = gridRowIdSelector(gridApiRef, props.leg);

	const [isChecked, setIsChecked] = useState(gridApiRef.current.isRowSelected(gridRowId));

	useEffect(() => {
		// Don't allow an invalid group to be selected when going big
		if (!isSmall && activeGroup >= groupCount) {
			setActiveGroup(groupCount - 1);
		}
	}, [isSmall, activeGroup, groupCount, setActiveGroup]);

	/**
	 * Determine the css grid area to assign based on index. Grid Areas couldn't be numbers.
	 */
	function getGridArea(i: number) {
		switch (i) {
			case 0:
				return !isSmall ? 'topleft' : 'top';
			case 1:
				return !isSmall ? 'topright' : 'bottom';
			case 2:
				return 'bottomleft';
			default:
				return 'bottomright';
		}
	}

	const onRowSelect = (event: ChangeEvent<HTMLInputElement>) => {
		setIsChecked(event.target.checked);
		if (event.target.checked) {
			gridApiRef.current.setRowSelectionModel({
				type: 'include',
				ids: new Set([...gridRowSelectionIdsSelector(gridApiRef).keys(), gridRowId])
			});
		} else {
			const currentSelectedRows = [...gridRowSelectionIdsSelector(gridApiRef).keys()];
			gridApiRef.current.setRowSelectionModel({
				type: 'include',
				ids: new Set([...currentSelectedRows.filter((item) => item !== gridRowId)])
			});
		}
	};

	return (
		<div className={styles.root}>
			<div className={`${styles.container} ${styles[`priority-${String(props.leg.legPriority)}`]}`}>
				<div className={styles['pinned-columns']}>
					{/* Always render the Selection Checkbox and Order Number */}
					<div className={styles['card-selector']}>
						<Checkbox
							checked={isChecked}
							onChange={onRowSelect}
							sx={{
								color: theme.palette.primary.main
							}}
						/>

						<RenderCardCell
							key={`${String(props.leg.legId)}-Order-Number`}
							columnDetails={dispatchColumnDefinitions.orderNumber}
							row={props.leg}
						/>
					</div>

					{// Render the pinned columns
					props.gridPinnedColumns.left
						?.filter((col) => !explicitlyRenderedPinnedColumns.includes(col))
						.map((item, index) => {
							const colDetails = props.gridColumnDetails.filter((col) => col.field === item);

							if (colDetails.length === 0) {
								return <></>;
							}

							return (
								<RenderCardCell
									key={`${String(props.leg.legId)}-pinned-${String(index)}`}
									columnDetails={colDetails[0]}
									row={props.leg}
									includeHeader
									headerStyle={{
										backgroundColor: 'rgba(0, 0, 0, 0.06)',
										fontSize: '14px',
										paddingLeft: '4px',
										paddingTop: '2px',
										paddingBottom: '2px'
									}}
								/>
							);
						})}
				</div>

				<div className={styles['columns-container']}>
					<Slick
						initialSlide={activeGroup}
						infinite={false}
						arrows={false}
						afterChange={(index) => {
							setActiveGroup(index);
						}}
						ref={slickRef}
					>
						{
							// Render remaining columns in groups of 4 or 2 (availableSlots)
							Array.from(Array(groupCount)).map((_, index) => {
								const startingRecord = index * availableSlots;

								// The outer most div is required for swipeable views to style, do not override or remove
								return (
									<div
										key={`${String(props.leg.legId)}-swipeable-${String(index)}`}
										className={styles['swipeable-container']}
									>
										<div className={styles['group-container']}>
											{renderingColumns.slice(startingRecord, startingRecord + availableSlots).map((item, index) => {
												return (
													<RenderCardCell
														key={`${String(props.leg.legId)}-column-${String(index)}`}
														columnDetails={item}
														row={props.leg}
														includeHeader
														headerStyle={{
															backgroundColor: 'rgba(0, 0, 0, 0.06)',
															fontSize: '14px',
															paddingLeft: '4px',
															paddingTop: '2px',
															paddingBottom: '2px',
															height: '24px'
														}}
														style={{
															gridArea: getGridArea(index),
															borderRight: !isSmall && (index === 0 || index === 2) ? `2px solid rgba(0, 0, 0, 0.06)` : '',
															maxHeight: '80px',
															height: '80px'
														}}
														renderEditable={props.renderEditable}
													/>
												);
											})}
										</div>
									</div>
								);
							})
						}
					</Slick>

					{groupCount > 1 && (
						<MobileStepper
							sx={{
								height: '20px',
								padding: '0px',
								width: '100%'
							}}
							activeStep={activeGroup}
							variant='dots'
							steps={groupCount}
							position='static'
							style={{ background: 'none' }}
							nextButton={
								<IconButton
									size='small'
									sx={{
										'&:hover': {
											backgroundColor: 'transparent'
										}
									}}
									onClick={() => {
										slickRef.current?.slickNext();
									}}
									disabled={activeGroup === groupCount - 1}
									disableRipple
								>
									<KeyboardArrowRight />
								</IconButton>
							}
							backButton={
								<IconButton
									size='small'
									sx={{
										'&:hover': {
											backgroundColor: 'transparent'
										}
									}}
									onClick={() => {
										slickRef.current?.slickPrev();
									}}
									disabled={activeGroup === 0}
									disableRipple
								>
									<KeyboardArrowLeft />
								</IconButton>
							}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
